import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { StatusCode, REACT_QUERY_KEYS } from '@hungryroot/rest';
import { postUpdateSpicyLevel } from '@hungryroot/rest/api/update_spicy_level/post';
import {
  CustomerIncludes,
  type CustomerIncludesAll,
} from '../../../components/customer/api';
import { type SpicyLevel } from '../../../components/SpicyButtons/spice-level';

interface PostUpdateSpicyLevelParams {
  spicy_level: SpicyLevel | null;
}

export function persistSpicePreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { data: PostUpdateSpicyLevelParams }) => {
      const { data } = params;
      const res = await postUpdateSpicyLevel(data);

      if (res.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(res.data, null, 2));
      }
      return res.data;
    },
    onMutate: async (newValue) => {
      await queryClient.cancelQueries({
        queryKey: [REACT_QUERY_KEYS.CUSTOMERS],
      });
      const existingCustomerQuery = queryClient.getQueriesData<{
        customer: CustomerIncludesAll;
      }>({
        queryKey: [
          REACT_QUERY_KEYS.CUSTOMERS,
          { includes: CustomerIncludes.All },
        ],
      });

      existingCustomerQuery.forEach(([queryKey, results]) => {
        const next = produce(results, (draft) => {
          if (draft === undefined) {
            return;
          }
          draft.customer.spicy_level = newValue.data.spicy_level;
        });
        queryClient.setQueryData(queryKey, next);
      });
      return { existingCustomerQuery, newValue };
    },
    onError: (
      err,
      newValue,
      context:
        | {
            existingCustomerQuery: unknown;
          }
        | undefined
    ) => {
      queryClient.setQueryData(
        [REACT_QUERY_KEYS.CUSTOMERS, { includes: CustomerIncludes.All }],
        context?.existingCustomerQuery
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [
          REACT_QUERY_KEYS.CUSTOMERS,
          { includes: CustomerIncludes.All },
        ],
      });
    },
  });
}
